import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  useTheme,
  useMediaQuery,
  Tooltip,
  Paper,
  Button,
} from '@mui/material';
import {
  Facebook,
  Instagram,
  LinkedIn,
  YouTube,
  Phone,
  Email,
  LocationOn,
  AccessTime,
  WhatsApp,
  Directions,
  ArrowForward,
  Place,
} from '@mui/icons-material';

// Custom JustDial Icon component
const JustDialIcon = () => (
  <Box
    sx={{
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      fontSize: '14px',
      fontFamily: "'Poppins', sans-serif",
    }}
  >
    JD
  </Box>
);

// Custom X (Twitter) Icon component
const XIcon = () => (
  <Box
    sx={{
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      fontSize: '18px',
      fontFamily: "'Poppins', sans-serif",
      color: 'inherit',
    }}
  >
    𝕏
  </Box>
);

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [contactInfo, setContactInfo] = useState(JSON.parse(localStorage.getItem('contactInfo') || JSON.stringify({
    address: 'Begur - Hulimavu Rd, Raghavendra Layout, Akshayanagara West, Krishna Layout, Akshayanagar, Bengaluru, Karnataka 560076',
    mapLink: 'https://www.google.com/maps/place/Careflex+physiotherapy+(Homecare+physio)/@12.8739635,77.6139061,17z/data=!3m1!4b1!4m6!3m5!1s0x3bae6b0027e775d5:0xa49ed9408152cd22!8m2!3d12.8739635!4f77.6139061!16s%2Fg%2F11ww3d8_yp?entry=ttu',
    phone: '+91 7619678243',
    email: 'info@careflexphysiotherapy.com',
    hours: 'Mon - Sun: 7:00 AM - 9:00 PM'
  })));
  const [locations, setLocations] = useState(JSON.parse(localStorage.getItem('locations') || '[]'));
  const [socialLinks, setSocialLinks] = useState(JSON.parse(localStorage.getItem('socialLinks') || JSON.stringify({
    facebook: 'https://www.facebook.com/careflexphysiotherapy',
    instagram: 'https://www.instagram.com/careflexphysiotherapy',
    twitter: 'https://twitter.com/careflexphysio',
    linkedin: 'https://www.linkedin.com/company/careflexphysiotherapy',
    youtube: 'https://www.youtube.com/@careflexphysiotherapy',
    justdial: 'https://jsdl.in/RSL-PWM1737629887'
  })));

  useEffect(() => {
    const handleStorageChange = () => {
      setContactInfo(JSON.parse(localStorage.getItem('contactInfo') || JSON.stringify(contactInfo)));
      setLocations(JSON.parse(localStorage.getItem('locations') || '[]'));
      setSocialLinks(JSON.parse(localStorage.getItem('socialLinks') || JSON.stringify(socialLinks)));
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const socialMediaLinks = [
    { platform: 'Facebook', icon: Facebook, url: socialLinks.facebook, color: '#1877f2' },
    { platform: 'Instagram', icon: Instagram, url: socialLinks.instagram, color: '#e4405f' },
    { platform: 'X (Twitter)', icon: XIcon, url: socialLinks.twitter, color: '#000000' },
    { platform: 'LinkedIn', icon: LinkedIn, url: socialLinks.linkedin, color: '#0077b5' },
    { platform: 'YouTube', icon: YouTube, url: socialLinks.youtube, color: '#ff0000' },
    { platform: 'JustDial', icon: JustDialIcon, url: socialLinks.justdial, color: '#FF0000' }
  ];

  const quickLinks = [
    { text: 'Home', href: '/' },
    { text: 'About Us', href: '/about' },
    { text: 'Services', href: '/services' },
    { text: 'Contact', href: '/contact' },
    { text: 'Book Appointment', href: '/book-appointment' },
  ];

  const FooterSection = ({ title, children }) => (
    <Box sx={{ height: '100%' }}>
      <Typography
        variant="h6"
        sx={{
          color: '#4CAF50',
          mb: 4,
          fontWeight: 600,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -12,
            left: 0,
            width: '50px',
            height: '3px',
            bgcolor: '#4CAF50',
            borderRadius: '2px',
          },
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );

  const formatAddress = (address) => {
    // Split the address into parts
    const parts = address.split(',');
    // Take the first part (street address) and the last two parts (city and state)
    const streetAddress = parts[0].trim();
    const city = parts[parts.length - 2]?.trim() || '';
    const state = parts[parts.length - 1]?.trim() || '';
    // Return a shorter version
    return `${streetAddress}, ${city}, ${state}`;
  };

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'rgba(0, 0, 0, 0.97)',
        color: 'white',
        py: 8,
        mt: 'auto',
        backdropFilter: 'blur(10px)',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
        },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={8}>
          {/* Quick Links Section */}
          <Grid item xs={12} md={6}>
            <FooterSection title="Quick Links">
              <List sx={{ mt: 2 }}>
                {quickLinks.map((item) => (
                  <ListItem key={item.text} disablePadding sx={{ mb: 2 }}>
                    <ListItemButton
                      component={Link}
                      href={item.href}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        borderRadius: '4px',
                        '&:hover': {
                          color: '#4CAF50',
                          bgcolor: 'rgba(255, 255, 255, 0.05)',
                          transform: 'translateX(5px)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                        <ArrowForward sx={{ fontSize: '1rem' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.text}
                        primaryTypographyProps={{
                          sx: { fontWeight: 500 }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </FooterSection>
          </Grid>

          {/* Contact Info Section */}
          <Grid item xs={12} md={6}>
            <FooterSection title="Contact Info">
              <List sx={{ mt: 2 }}>
                <ListItem disablePadding sx={{ mb: 4 }}>
                  <ListItemIcon sx={{ minWidth: 36, color: 'rgba(255, 255, 255, 0.8)' }}>
                    <LocationOn />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Tooltip title={contactInfo.address} arrow>
              <Typography
                            variant="body2"
                sx={{
                              color: 'rgba(255, 255, 255, 0.9)',
                              textDecoration: 'none',
                              fontWeight: 500,
                              display: 'block',
                              cursor: 'pointer',
                              '&:hover': {
                  color: '#4CAF50',
                                textDecoration: 'underline'
                              },
                            }}
                          >
                            {formatAddress(contactInfo.address)}
              </Typography>
                        </Tooltip>
                        <Link
                          href={contactInfo.mapLink || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contactInfo.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                sx={{
                            color: '#4CAF50',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            fontSize: '0.875rem',
                            '&:hover': {
                              textDecoration: 'underline',
                              color: '#45a049'
                            },
                          }}
                        >
                          <Directions sx={{ fontSize: '1rem', mr: 0.5 }} />
                          Get Directions
                        </Link>
                      </Box>
                    }
                  />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 4 }}>
                  <ListItemIcon sx={{ minWidth: 36, color: 'rgba(255, 255, 255, 0.8)' }}>
                    <Phone />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                  <Link
                        href={`tel:${contactInfo.phone}`}
                    sx={{
                          color: 'rgba(255, 255, 255, 0.9)',
                      textDecoration: 'none',
                          fontWeight: 500,
                      '&:hover': {
                        color: '#4CAF50',
                            textDecoration: 'underline'
                          },
                        }}
                      >
                        {contactInfo.phone}
                      </Link>
                    }
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 36, color: 'rgba(255, 255, 255, 0.8)' }}>
                    <Email />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Link
                        href={`mailto:${contactInfo.email}`}
                        sx={{
                          color: 'rgba(255, 255, 255, 0.9)',
                          textDecoration: 'none',
                          fontWeight: 500,
                          '&:hover': { 
                            color: '#4CAF50',
                            textDecoration: 'underline'
                          },
                        }}
                      >
                        {contactInfo.email}
                  </Link>
                    }
                  />
                </ListItem>
              </List>
            </FooterSection>
          </Grid>

          {/* Other Locations Section */}
          <Grid item xs={12} md={6}>
            <FooterSection title="Other Locations">
              <List sx={{ mt: 2 }}>
                {locations.length === 0 ? (
                  <ListItem disablePadding>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontStyle: 'italic'
                      }}
                    >
                      No locations available at the moment.
                    </Typography>
                  </ListItem>
                ) : (
                  locations.slice(1).map((location) => (
                    <ListItem 
                      key={location.id} 
                      disablePadding 
                      sx={{ 
                        mb: 4,
                        '&:last-child': { mb: 0 }
                      }}
                    >
                      <ListItemButton
                        component={Link}
                        href={location.mapLink || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.fullAddress)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          borderRadius: '8px',
                          bgcolor: 'rgba(255, 255, 255, 0.05)',
                          transition: 'all 0.3s ease-in-out',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: '#4CAF50' }}>
                          <Place />
                        </ListItemIcon>
                        <ListItemText
                          primary={
              <Typography
                              variant="body2" 
                sx={{
                                color: 'rgba(255, 255, 255, 0.9)', 
                  fontWeight: 600,
                                mb: 0.5
                }}
              >
                              {location.name}
                              {location.branch && ` (${location.branch})`}
              </Typography>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              <Typography 
                                variant="body2" 
                sx={{
                                  color: 'rgba(255, 255, 255, 0.7)',
                                  mb: 1,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                              >
                                {location.fullAddress}
                              </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                                flexWrap: 'wrap'
                }}>
                  <Link
                                  href={`tel:${location.phone}`}
                    sx={{
                                    color: '#4CAF50',
                      textDecoration: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    fontSize: '0.875rem',
                                    '&:hover': { 
                                      textDecoration: 'underline',
                                      color: '#45a049'
                                    },
                                  }}
                                >
                                  <Phone sx={{ fontSize: '1rem', mr: 0.5 }} />
                                  {location.phone}
                  </Link>
                  <Link
                                  href={location.mapLink || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.fullAddress)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                    sx={{
                                    color: '#4CAF50',
                      textDecoration: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    fontSize: '0.875rem',
                                    '&:hover': { 
                                      textDecoration: 'underline',
                                      color: '#45a049'
                                    },
                                  }}
                                >
                                  <Directions sx={{ fontSize: '1rem', mr: 0.5 }} />
                                  Get Directions
                  </Link>
                </Box>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))
                )}
              </List>
            </FooterSection>
          </Grid>

          {/* Connect With Us Section */}
          <Grid item xs={12} md={6}>
            <FooterSection title="Connect With Us">
                <Box sx={{ 
                  display: 'flex', 
                flexWrap: 'wrap', 
                gap: 3, 
                mb: 6,
                mt: 2,
                justifyContent: { xs: 'center', sm: 'flex-start' }
              }}>
                {socialMediaLinks.map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <Tooltip key={index} title={social.platform}>
                      <IconButton
                        component="a"
                        href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                          color: 'white',
                          width: 56,
                          height: 56,
                          transition: 'all 0.3s ease-in-out',
                          '&:hover': {
                            bgcolor: social.color,
                            transform: 'translateY(-5px)',
                            boxShadow: `0 5px 15px ${social.color}40`,
                          },
                        }}
                      >
                        <Icon sx={{ fontSize: '1.75rem' }} />
                      </IconButton>
                    </Tooltip>
                  );
                })}
                </Box>
              <Box>
                <Button
                  variant="contained"
                  startIcon={<WhatsApp />}
                  href={`https://wa.me/${contactInfo.phone.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    bgcolor: '#25D366',
                    color: 'white',
                    width: '100%',
                    py: 2,
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      bgcolor: '#128C7E',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 5px 15px rgba(37, 211, 102, 0.3)',
                    },
                  }}
                >
                  Chat on WhatsApp
                </Button>
            </Box>
            </FooterSection>
          </Grid>
        </Grid>

        {/* Bottom Section */}
        <Box sx={{ 
          mt: 8, 
          pt: 6, 
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -1,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
          },
        }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: { xs: 'center', sm: 'flex-start' }
              }}>
                <AccessTime sx={{ fontSize: '1rem' }} />
                {contactInfo.hours}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: { xs: 'center', sm: 'flex-end' }, 
                gap: 3,
                flexWrap: 'wrap',
                '& a': {
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -2,
                    left: 0,
                    width: '0%',
                    height: '1px',
                    bgcolor: '#4CAF50',
                    transition: 'width 0.3s ease-in-out',
                  },
                  '&:hover::after': {
                    width: '100%',
                  },
                },
              }}>
                <Link
                  href="/privacy-policy"
          sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    textDecoration: 'none',
                    '&:hover': { color: '#4CAF50' },
                  }}
                >
                  Privacy Policy
                </Link>
                <Link
                  href="/terms"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
                    textDecoration: 'none',
                    '&:hover': { color: '#4CAF50' },
                  }}
                >
                  Terms of Service
                </Link>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer; 